import { MainApi } from 'api/MainApi'
import { RobotApi } from 'api/RobotApi'
import { MainService, OdysseyRead, RobotRead } from 'api/schema'
import Box from 'components/Box/Box'
import { Button } from 'components/Button/Button'
import Dropdown, { IOption } from 'components/Dropdown/Dropdown'
import Loader from 'components/Loader/Loader'
import MapboxCommon from 'components/MapboxMap/MapboxCommon'
import Title from 'components/Title/Title'
import { Feature } from 'geojson'
import CleaningPointsOnMap from 'pages/Mission/MissionView/MissionMap/Layers/CleaningPointsOnMap'
import CleaningRoutesOnMap from 'pages/Mission/MissionView/MissionMap/Layers/CleaningRoutesOnMap'
import CleaningZonesNamesOnMap from 'pages/Mission/MissionView/MissionMap/Layers/CleaningZonesNamesOnMap'
import CleaningZonesOnMap from 'pages/Mission/MissionView/MissionMap/Layers/CleaningZonesOnMap'
import MissionStartFinishPoints from 'pages/Mission/MissionView/MissionMap/Layers/MissionStartFinishPoints'
import RestrictZonesOnMap from 'pages/Mission/MissionView/MissionMap/Layers/RestrictZonesOnMap'
import TransferRoutesOnMap from 'pages/Mission/MissionView/MissionMap/Layers/TransferRoutesOnMap'
import SelectMissionModal from 'pages/Robot/RobotView/RobotLeftBlock/RobotDetails/RobotMissionMainStatus/SelectMissionModal/SelectMissionModal'
import TaskSettingsModal from 'pages/TaskScheduler/TaskSettingsModal/TaskSettingsModal'
import { useUrlQueryState } from 'hooks/useUrlQueryState'
import { useCallback, useEffect, useRef, useState, type SetStateAction } from 'react'
import { useTranslation } from 'react-i18next'
import { MapboxMap } from 'react-map-gl'
import { useNavigate } from 'react-router-dom'
import { MISSION_ROBOT_TYPES, PATH, PROGRESS_TYPES } from 'utils/constants'
import { applyStateAction, optionalIntParam, pageParam, stringParam } from 'utils/urlQueryState'
import { IMapboxElement, IMission, IMissionInfo } from 'utils/interfaces'
import { zoomMapTo } from 'utils/mapUtils'

import AddMissionModal from './AddMissionModal/AddMissionModal'
import AddRobotModal from './AddRobotModal/AddRobotModal'
import MissionTable from './MissionTable/MissionTable'

import style from './MissionList.module.css'
import mapPageFilterDropdown from 'styles/mapPageFilterDropdown.module.css'
import pageLayoutStyle from 'styles/pageLayout.module.css'
import tablePagination from 'styles/tablePagination.module.css'

const MissionList = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [url, setUrl] = useUrlQueryState({
    q: stringParam(),
    robotType: stringParam('All'),
    page: pageParam(),
    mission: optionalIntParam(),
  })
  const [loadingProgress, setLoadingProgress] = useState(PROGRESS_TYPES.IDLE)
  const [creatingMissionProgress, setCreatingMissionProgress] = useState(PROGRESS_TYPES.IDLE)
  const [missions, setMissions] = useState<IMissionInfo[]>([])
  const [odysseys, setOdysseys] = useState<OdysseyRead[]>([])
  const [robotTypeOptions, setRobotTypeOptions] = useState<{ display_name: string; robot_type: string }[]>([])
  const [isAddMissionModalOpen, setIsAddMissionModalOpen] = useState(false)
  const [isAddRobotModalOpen, setIsAddRobotModalOpen] = useState(false)
  const currentPage = url.page
  const setCurrentPage = (page: SetStateAction<number>) =>
    setUrl(s => ({ page: applyStateAction(s.page, page) }))
  const [pageInput, setPageInput] = useState(String(url.page))
  const selectedRobotType = url.robotType || 'All'
  const setSelectedRobotType = (robotType: string) => setUrl({ robotType, page: 1 })
  const searchQuery = url.q
  const setSearchQuery = (q: string) => setUrl({ q })
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(url.q)
  const skipPageResetRef = useRef(true)
  const [totalCount, setTotalCount] = useState(0)
  const [AddtoScenarioMissionId, setAddtoScenarioMissionId] = useState<number | undefined>(undefined)
  const [isLaunchModalOpen, setIsLaunchModalOpen] = useState(false)
  const [launchItem, setLaunchItem] = useState<{ mission?: IMissionInfo } | undefined>(undefined)
  const lastFetchedPage = useRef<string | null>(null)
  const PAGE_SIZE = 20

  // Map state
  const mapRef = useRef<IMapboxElement>(null)
  const [map, setMap] = useState<MapboxMap | undefined>(undefined)
  const highlightedMissionId = url.mission
  const setHighlightedMissionId = (id: number | undefined) => setUrl({ mission: id })
  const [selectedMissionDetails, setSelectedMissionDetails] = useState<IMission | undefined>(undefined)

  // Cache data to avoid refetching
  const dataCache = useRef({
    robots: [] as RobotRead[],
    fetched: { robots: false },
  })

  const onMapInited = () => {
    const m = mapRef.current?.getMap()
    if (m) setMap(m)
  }

  // Load selected mission details for the map
  useEffect(() => {
    const fetchMissionDetails = async () => {
      if (highlightedMissionId) {
        try {
          const missionData = await MainApi.fetchMission(highlightedMissionId)
          setSelectedMissionDetails(missionData)
        } catch (error) {
          console.error('Failed to fetch selected mission details', error)
        }
      } else {
        setSelectedMissionDetails(undefined)
      }
    }
    fetchMissionDetails()
  }, [highlightedMissionId])

  // Zoom map to selected mission
  useEffect(() => {
    if (map && selectedMissionDetails) {
      const features: Feature[] = [
        ...selectedMissionDetails.cleaningZones,
        ...selectedMissionDetails.restrictZones,
        ...selectedMissionDetails.cleaningRoutes,
        ...selectedMissionDetails.transferRoutes,
        ...(selectedMissionDetails.pointRoutes || []),
      ]
      if (features.length > 0) {
        zoomMapTo(map, features, 580)
      }
    }
  }, [map, selectedMissionDetails])

  // Fetch initial data if not already cached
  const fetchInitialData = useCallback(async () => {
    if (!dataCache.current.fetched.robots) {
      const responseRobots = await MainService.mainRobotList()
      dataCache.current.robots = responseRobots.results ?? []
      // setRobots(dataCache.current.robots)
      setOdysseys(dataCache.current.robots.flatMap(robot => robot.odysseys))
      dataCache.current.fetched.robots = true

      const options = await RobotApi.fetchMissionRobotTypeOptions()
      setRobotTypeOptions(options.length > 0 ? options : [{ display_name: 'Sweeper', robot_type: 'Sweeper' }])
    }
  }, [])

  const fetchData = useCallback(
    async (forceRefresh = false) => {
      const cacheKey = `${currentPage}_${selectedRobotType}_${debouncedSearchQuery}`
      if (!forceRefresh && lastFetchedPage.current === cacheKey) return
      lastFetchedPage.current = cacheKey

      try {
        setLoadingProgress(PROGRESS_TYPES.WORK)
        // Fetch initial data once
        await fetchInitialData()

        const { results, count } = await MainApi.fetchMissions(
          currentPage,
          PAGE_SIZE,
          selectedRobotType === 'All' ? undefined : selectedRobotType,
          debouncedSearchQuery || undefined
        )
        setMissions(results)
        setTotalCount(count)
        setLoadingProgress(PROGRESS_TYPES.SUCCESS)
      } catch (error) {
        if (error instanceof TypeError && error.message === 'Failed to fetch') {
          setLoadingProgress(PROGRESS_TYPES.IDLE)
          return
        }
        setLoadingProgress(PROGRESS_TYPES.ERROR)
        console.warn('[MissionList] fetchData error:', error)
      }
    },
    [currentPage, fetchInitialData, selectedRobotType, debouncedSearchQuery]
  )

  useEffect(() => {
    fetchData()
  }, [fetchData])

  useEffect(() => {
    if (skipPageResetRef.current) return
    if (currentPage !== 1) setCurrentPage(1)
  }, [selectedRobotType, debouncedSearchQuery])

  useEffect(() => {
    skipPageResetRef.current = false
  }, [])

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery)
    }, 300)
    return () => clearTimeout(timer)
  }, [searchQuery])

  useEffect(() => {
    setPageInput(currentPage.toString())
  }, [currentPage])

  const handlePageJump = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      const val = parseInt(pageInput)
      const maxPage = Math.ceil(totalCount / PAGE_SIZE) || 1
      if (!isNaN(val) && val >= 1 && val <= maxPage) {
        setCurrentPage(val)
      } else {
        setPageInput(currentPage.toString())
      }
    }
  }

  const onCloseClicked = () => {
    setIsAddMissionModalOpen(false)
    setIsAddRobotModalOpen(false)
  }

  const onSelectClicked = (
    robotType: string,
    name: string,
    locMode: 'GNSS' | 'SLAM',
    mapId?: number,
    mapName?: string
  ) => {
    createNewMission(robotType, name, locMode, mapId, mapName)
    setIsAddMissionModalOpen(false)
  }

  const createButtonPressed = () => {
    if (robotTypeOptions.length === 0) {
      setIsAddRobotModalOpen(true)
    } else {
      setIsAddMissionModalOpen(true)
    }
  }

  const createNewMission = async (
    robotType: string | null = null,
    name: string = 'New Mission',
    locMode: 'GNSS' | 'SLAM' = 'GNSS',
    mapId?: number,
    mapName?: string
  ) => {
    try {
      setCreatingMissionProgress(PROGRESS_TYPES.WORK)
      const rType = robotType ? robotType : robotTypeOptions[0].robot_type
      const mission = await MainApi.createMission(name, rType, '')
      if (locMode === 'SLAM' && mapName) {
        await MainApi.changeLocationName(mission.id, mapName)
      }
      setCreatingMissionProgress(PROGRESS_TYPES.SUCCESS)
      navigate(`${PATH.MISSION}${mission.id}`)
    } catch (error) {
      setCreatingMissionProgress(PROGRESS_TYPES.ERROR)
      console.log('Error creating mission: ', error)
    }
  }

  const handleScenarioSelected = async (scenario: any) => {
    if (!AddtoScenarioMissionId || !scenario) return

    try {
      // 1. Fetch the latest scenario details to get the current mission list
      const fullScenario = await MainApi.fetchScenario(scenario.id)
      const currentMissions = fullScenario.missions_ids || []

      // 2. Add the mission if it's not already there
      if (!currentMissions.includes(AddtoScenarioMissionId)) {
        const isFirst = currentMissions.length === 0
        const patchData: any = {
          missions_ids: [...currentMissions, AddtoScenarioMissionId],
        }

        if (isFirst) {
          const missionDetails = await MainApi.fetchMission(AddtoScenarioMissionId)
          if (missionDetails?.mission?.robot_type) {
            patchData.robot_type = missionDetails.mission.robot_type
          }
        }

        await MainApi.patchScenarios(scenario.id, patchData)
      }

      setAddtoScenarioMissionId(undefined)
    } catch (error) {
      console.error('Failed to add mission to scenario', error)
    }
  }

  return (
    <div className={style.container}>
      <div className={style.main}>
        <div className={style.headerArea}>
          <div className={style.titleBlock}>
            <Title text={t('missions.title')} />
            <Button.Standart
              className={style.button}
              onClick={createButtonPressed}
              loading={creatingMissionProgress === PROGRESS_TYPES.WORK}
            >
              New Mission
            </Button.Standart>
          </div>
          <div className={style.controlsBlock}>
            {robotTypeOptions.length > 1 && (
              <Dropdown
                className={mapPageFilterDropdown.filterDropdown}
                minWidth={200}
                currentOption={
                  selectedRobotType !== 'All'
                    ? {
                        value: selectedRobotType,
                        text:
                          robotTypeOptions.find(opt => opt.robot_type === selectedRobotType)?.display_name ||
                          selectedRobotType,
                      }
                    : undefined
                }
                placeholder="All Robot Types"
                options={[
                  { value: 'All', text: 'All Robot Types' },
                  ...Array.from(
                    (() => {
                      const m = new Map<string, string>()
                      for (const opt of robotTypeOptions) {
                        if (!m.has(opt.robot_type)) m.set(opt.robot_type, opt.display_name)
                      }
                      return m.entries()
                    })()
                  ).map(([robot_type, display_name]) => ({
                    value: robot_type,
                    text: display_name,
                  })),
                ]}
                onClickOption={(opt: IOption) => setSelectedRobotType(String(opt.value))}
              />
            )}
            <input
              className={style.searchInput}
              type="text"
              placeholder="Search missions..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
          </div>
        </div>
        <div className={style.contentContainer}>
          <Box className={style.box}>
            <div className={style.tableWrapper}>
              {loadingProgress === PROGRESS_TYPES.WORK && missions.length === 0 ? (
                <Loader />
              ) : loadingProgress === PROGRESS_TYPES.ERROR ? (
                <div>Error loading missions</div>
              ) : (
                <MissionTable
                  missions={missions}
                  odysseys={odysseys}
                  highlightedMissionId={highlightedMissionId}
                  onMissionClick={id => setHighlightedMissionId(id)}
                  onMissionsUpdated={(deletedId?: number) => {
                    if (deletedId && highlightedMissionId === deletedId) {
                      setHighlightedMissionId(undefined)
                      setSelectedMissionDetails(undefined)
                    }
                    dataCache.current.fetched.robots = false
                    fetchData(true)
                  }}
                  onLaunchMission={mission => {
                    setLaunchItem({ mission })
                    setIsLaunchModalOpen(true)
                  }}
                  onAddtoScenario={id => setAddtoScenarioMissionId(id)}
                />
              )}
              {totalCount > PAGE_SIZE && loadingProgress !== PROGRESS_TYPES.WORK && (
                <div className={tablePagination.pagination}>
                  <button
                    className={tablePagination.paginationButton}
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                  >
                    &larr;
                  </button>
                  <span className={tablePagination.pageInfo}>
                    <input
                      className={tablePagination.pageInput}
                      value={pageInput}
                      onChange={e => setPageInput(e.target.value)}
                      onKeyDown={handlePageJump}
                      onBlur={() => setPageInput(currentPage.toString())}
                    />
                    / {Math.ceil(totalCount / PAGE_SIZE) || 1}
                  </span>
                  <button
                    className={tablePagination.paginationButton}
                    onClick={() => setCurrentPage(prev => prev + 1)}
                    disabled={currentPage * PAGE_SIZE >= totalCount}
                  >
                    &rarr;
                  </button>
                </div>
              )}
            </div>
          </Box>
          {/* Map on the right - always mounted */}
          <div className={style.mapWrapper}>
            <MapboxCommon
              ref={mapRef}
              disableRuler={true}
              overlayItems={[]}
              onMapInited={onMapInited}
              style={{ borderRadius: 0 }}
            >
              {selectedMissionDetails && (
                <>
                  <RestrictZonesOnMap restrictZones={selectedMissionDetails.restrictZones} />
                  <CleaningZonesOnMap cleaningZones={selectedMissionDetails.cleaningZones} />
                  <CleaningRoutesOnMap cleaningRoutes={selectedMissionDetails.cleaningRoutes} />
                  <TransferRoutesOnMap
                    transferRoutes={[
                      ...selectedMissionDetails.transferRoutes,
                      ...(selectedMissionDetails.pointRoutes || []),
                    ]}
                  />
                  <CleaningPointsOnMap
                    cleaningRoutes={selectedMissionDetails.cleaningRoutes}
                    launchPoints={selectedMissionDetails.launchPoints}
                  />
                  <MissionStartFinishPoints
                    startPoint={selectedMissionDetails.startingPoint}
                    finishPoint={selectedMissionDetails.finishingPoint}
                  />
                </>
              )}
            </MapboxCommon>
          </div>
        </div>
      </div>
      <AddMissionModal
        isOpen={isAddMissionModalOpen}
        onClose={onCloseClicked}
        onSelect={onSelectClicked}
        robotTypeOptions={robotTypeOptions}
      />
      <AddRobotModal isOpen={isAddRobotModalOpen} onClose={onCloseClicked} />
      <SelectMissionModal
        isOpen={!!AddtoScenarioMissionId}
        onClose={() => setAddtoScenarioMissionId(undefined)}
        onConfirmClick={handleScenarioSelected}
        robotType={missions.find(m => m.id === AddtoScenarioMissionId)?.robot_type}
        forceScenariosOnly={true}
        allowEmptyScenarios={true}
      />
      <TaskSettingsModal
        isOpen={isLaunchModalOpen}
        taskName=""
        onClose={() => {
          setIsLaunchModalOpen(false)
          setLaunchItem(undefined)
        }}
        onSubmit={() => {
          setIsLaunchModalOpen(false)
          setLaunchItem(undefined)
          dataCache.current.fetched.robots = false
          fetchData(true)
        }}
        target={{ kind: 'new' }}
        preselectedData={launchItem?.mission ? { mission: launchItem.mission } : undefined}
        dateToEdit={null}
      />
    </div>
  )
}

export default MissionList
